import { logger } from '@playbooks/utils/logger';

import Algoliasearch, { SearchIndex } from 'algoliasearch';

const ALGOLIA_ID = process.env.ALGOLIA_APP_ID;
const ALGOLIA_KEY = process.env.ALGOLIA_APP_KEY;

class AlgoliaApiService {
	/* ----- Computed ----- */
	static get client() {
		return Algoliasearch(ALGOLIA_ID, ALGOLIA_KEY);
	}

	/* ----- Helpers ----- */
	static buildIndex(tableName): SearchIndex {
		return this.client.initIndex(tableName);
	}

	/* ----- Methods ----- */
	static async createIndex(tableName, settings = {}): Promise<any> {
		const index = this.buildIndex(tableName);
		return await index.setSettings(settings).wait();
	}

	static async updateIndex(tableName, settings = {}): Promise<any> {
		const index = this.buildIndex(tableName);
		return await index.setSettings(settings).wait();
	}

	static async pingIndex(tableName) {
		const index = this.buildIndex(tableName);
		return await index.exists();
	}

	static async clearIndex(tableName): Promise<any> {
		const index = this.buildIndex(tableName);
		return await index.clearObjects().wait();
	}

	static async createObject(tableName, object): Promise<any> {
		const index = this.buildIndex(tableName);
		const objectID = object.uuid.toString();
		return await index.saveObject({ ...object, objectID }).wait();
	}

	static async updateObject(tableName, object): Promise<any> {
		const index = this.buildIndex(tableName);
		const objectID = object.uuid.toString();
		return await index.partialUpdateObject({ ...object, objectID }, { createIfNotExists: true }).wait();
	}

	static async updateObjects(tableName, objects = []): Promise<any> {
		const index = this.buildIndex(tableName);
		objects.map(object => (object.objectID = object.uuid.toString()));
		return await index.saveObjects(objects, { autoGenerateObjectIDIfNotExist: true }).wait();
	}

	static async deleteObject(tableName, objectId): Promise<any> {
		const index = this.buildIndex(tableName);
		return await index.deleteObject(objectId).wait();
	}

	static async deleteIndex(tableName): Promise<any> {
		const index = this.buildIndex(tableName);
		return await index.delete().wait();
	}

	static async deleteIndices(): Promise<any> {
		const { items } = await this.client.listIndices();
		if (items?.length === 0) return logger.log('No indices to delete.');
		const options = items.map(({ name }) => ({ indexName: name, action: 'delete' }));
		return await this.client.multipleBatch(options as any).wait();
	}
}

export { AlgoliaApiService };

// Docs
// https://www.npmjs.com/package/algoliasearch
// https://www.algolia.com/doc/api-reference/api-parameters/searchableAttributes/#examples
// https://www.algolia.com/doc/api-reference/api-methods/partial-update-objects/#examples
